import { useState } from 'react';
import { api } from '../../../utils/api';
import { toast } from '../../ui/simple-toaster';

/**
 * Hook encapsulating all work/rest segment actions:
 * startWork, startRest, doStartWork, doStartRest, odometer, fuel stop, night rest, rest location.
 */
export function useWorkRestActions({
  driverInfo, driverState, currentSegment, vehicleInfo, isResting, isWorking, elapsedTime,
  setCurrentSegment, setElapsedTime, loadDriverData,
  setShowOdometerConfirm, setShowNightRestConfirm, setShowRestLocationModal,
  selectedVehicle, setSelectedVehicle, pendingTrailers, setPendingTrailers,
}) {
  const [confirmedOdometer, setConfirmedOdometer] = useState(null);
  const [odometerLoading, setOdometerLoading] = useState(false);
  const [pendingWorkStart, setPendingWorkStart] = useState(false);
  const [pendingRestStart, setPendingRestStart] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [nightRestConfirmType, setNightRestConfirmType] = useState(null);
  const [nightRestMessage, setNightRestMessage] = useState('');

  // ── Start Work ──────────────────────────────────────────────
  const startWork = async () => {
    if (!driverInfo || !driverState) return;
    if (vehicleInfo && vehicleInfo.last_odometer !== undefined) {
      setPendingWorkStart(true);
      setShowOdometerConfirm(true);
      return;
    }
    await checkNightRestAndStartWork();
  };

  const checkNightRestAndStartWork = async (odometerReading = null) => {
    const isNightRestActive = isResting && currentSegment?.source === 'NIGHT_REST';

    if (isNightRestActive) {
      if (driverState?.can_start_work !== false) {
        await doStartWork(odometerReading);
        return;
      }

      const workHoursUsed = ((driverState?.work_24h_seconds || 0) + (isWorking ? elapsedTime : 0)) / 3600;
      const workHoursAvailable = Math.max(0, 12 - workHoursUsed);

      if (workHoursAvailable <= 0) {
        setNightRestMessage('You do not have enough hours to start work');
        setNightRestConfirmType('WORK_BLOCKED');
        setShowNightRestConfirm(true);
        return;
      }

      const now = new Date();
      const currentHour = now.getHours();
      const isBeforeOneAM = currentHour >= 22 || currentHour < 1;

      if (isBeforeOneAM && driverState?.one_am_constraint?.applies) {
        const remainingHours = driverState?.one_am_constraint?.remaining_hours || 0;
        setNightRestMessage(`You have ${remainingHours.toFixed(1)} hours available before 1AM`);
        setNightRestConfirmType('WORK_1AM');
        setShowNightRestConfirm(true);
        return;
      }

      setNightRestMessage('Night Rest has not completed the required hours. If you proceed, you will lose this Night Rest');
      setNightRestConfirmType('WORK');
      setShowNightRestConfirm(true);
      return;
    }

    await doStartWork(odometerReading);
  };

  const doStartWork = async (odometerReading = null) => {
    if (!driverInfo) return;
    const odoValue = odometerReading ?? confirmedOdometer;

    const now = new Date();
    const optimisticSegment = {
      segment_id: `local-${Date.now()}`,
      segment_type: 'WORK',
      start_ts: now.toISOString(),
      end_ts: null,
      vehicle_id: vehicleInfo?.vehicle_id || null,
      odometer_start: odoValue,
      isLocal: true
    };

    setCurrentSegment(optimisticSegment);
    setElapsedTime(0);
    setIsPending(true);

    try {
      const diariesRes = await api(`/drivers/${driverInfo.driver_id}/diaries`);
      const activeDiary = diariesRes.diaries?.find(d => d.active_flag);
      if (!activeDiary) throw new Error('No active diary found');

      const body = {
        driver_id: driverInfo.driver_id,
        diary_id: activeDiary.diary_id,
        segment_type: 'WORK',
        vehicle_id: vehicleInfo?.vehicle_id || null,
        source: 'DRIVER_APP'
      };
      if (odoValue != null) body.odometer_start = odoValue;

      const result = await api('/segments/start', { method: 'POST', body: JSON.stringify(body) });

      if (result.segment_id) {
        setCurrentSegment(prev => ({ ...prev, segment_id: result.segment_id, isLocal: false }));

        // Assign trailers to this WORK segment if any were selected
        if (pendingTrailers && pendingTrailers.length > 0) {
          api(`/segments/${result.segment_id}/trailers`, {
            method: 'POST',
            body: JSON.stringify(pendingTrailers)
          }).then(() => {
            if (setPendingTrailers) setPendingTrailers([]);
          }).catch(err => console.warn('Trailer assignment failed:', err));
        }
      }

      if (odoValue != null && vehicleInfo?.vehicle_id) {
        api('/odometer/confirm', {
          method: 'POST',
          body: JSON.stringify({ vehicle_id: vehicleInfo.vehicle_id, confirmed_reading: odoValue })
        }).catch(err => console.warn('Odometer confirm failed (segment already created):', err));
      }

      loadDriverData();
    } catch (err) {
      setCurrentSegment(null);
      toast.error('Failed to start work: ' + err.message);
    } finally {
      setIsPending(false);
    }
  };

  // ── Start Rest ──────────────────────────────────────────────
  const startRest = async () => {
    if (!driverInfo || !driverState) return;
    const isNightRestActive = isResting && currentSegment?.source === 'NIGHT_REST';

    if (isNightRestActive) {
      if (driverState?.can_start_work !== false) {
        setPendingRestStart(true);
        setShowRestLocationModal(true);
        return;
      }
      setNightRestMessage('You are already on Night Rest. Do you want to convert this NIGHT REST to REST?');
      setNightRestConfirmType('REST');
      setShowNightRestConfirm(true);
      return;
    }

    setPendingRestStart(true);
    setShowRestLocationModal(true);
  };

  const doStartRest = async (location = null) => {
    if (!driverInfo) return;

    const now = new Date();
    const optimisticSegment = {
      segment_id: `local-${Date.now()}`,
      segment_type: 'REST',
      start_ts: now.toISOString(),
      end_ts: null,
      vehicle_id: null,
      location_address: location?.address,
      location_suburb: location?.suburb,
      isLocal: true
    };

    setCurrentSegment(optimisticSegment);
    setElapsedTime(0);
    setIsPending(true);

    try {
      const diariesRes = await api(`/drivers/${driverInfo.driver_id}/diaries`);
      const activeDiary = diariesRes.diaries?.find(d => d.active_flag);
      if (!activeDiary) throw new Error('No active diary found');

      const result = await api('/segments/start', {
        method: 'POST',
        body: JSON.stringify({
          driver_id: driverInfo.driver_id,
          diary_id: activeDiary.diary_id,
          segment_type: 'REST',
          vehicle_id: null,
          source: 'DRIVER_APP',
          location: location ? {
            address: location.address,
            suburb: location.suburb,
            lat: location.lat,
            lng: location.lng,
            source: location.source || 'GPS'
          } : null
        })
      });

      if (result.segment_id) {
        setCurrentSegment(prev => ({ ...prev, segment_id: result.segment_id, isLocal: false }));
      }

      loadDriverData();
    } catch (err) {
      setCurrentSegment(null);
      toast.error('Failed to start rest: ' + err.message);
    } finally {
      setIsPending(false);
    }
  };

  // ── Handlers ────────────────────────────────────────────────
  const handleNightRestConfirmProceed = async () => {
    setShowNightRestConfirm(false);
    if (nightRestConfirmType === 'WORK' || nightRestConfirmType === 'WORK_1AM') {
      await doStartWork(confirmedOdometer);
    } else if (nightRestConfirmType === 'REST') {
      await doStartRest();
    }
  };

  const handleOdometerConfirm = async ({ vehicle_id, confirmed_reading, was_edited, source }) => {
    setOdometerLoading(true);
    try {
      if (vehicleInfo) {
        const updatedVehicle = { ...vehicleInfo, last_odometer: confirmed_reading };
        setSelectedVehicle(updatedVehicle);
        localStorage.setItem('lawd_selected_vehicle', JSON.stringify(updatedVehicle));
      }
      toast.success(`Odometer confirmed: ${confirmed_reading.toLocaleString()} km`);
      setShowOdometerConfirm(false);
      setConfirmedOdometer(confirmed_reading);

      if (pendingWorkStart) {
        setPendingWorkStart(false);
        await checkNightRestAndStartWork(confirmed_reading);
      }
    } catch (err) {
      toast.error('Failed to confirm odometer: ' + err.message);
    } finally {
      setOdometerLoading(false);
    }
  };

  const handleFuelStop = async ({ vehicle_id, odometer_reading, fuel_amount_litres, fuel_cost, location }) => {
    setOdometerLoading(true);
    try {
      const result = await api('/odometer/fuel-stop', {
        method: 'POST',
        body: JSON.stringify({ vehicle_id, odometer_reading, fuel_amount_litres, fuel_cost, location })
      });

      if (vehicleInfo) {
        const updatedVehicle = { ...vehicleInfo, last_odometer: odometer_reading };
        setSelectedVehicle(updatedVehicle);
        localStorage.setItem('lawd_selected_vehicle', JSON.stringify(updatedVehicle));
      }

      toast.success(result.message || 'Fuel stop recorded');
      if (result.can_start_rest) toast.info('You can now take a rest break if needed');
    } catch (err) {
      toast.error('Failed to record fuel stop: ' + err.message);
    } finally {
      setOdometerLoading(false);
    }
  };

  const handleRestLocationConfirm = async (location) => {
    setShowRestLocationModal(false);
    await doStartRest(location);
    setPendingRestStart(false);
  };

  const handleRestLocationClose = () => {
    setShowRestLocationModal(false);
    setPendingRestStart(false);
  };

  return {
    startWork, startRest,
    handleNightRestConfirmProceed,
    handleOdometerConfirm, handleFuelStop,
    handleRestLocationConfirm, handleRestLocationClose,
    isPending, odometerLoading, pendingWorkStart, setPendingWorkStart,
    nightRestConfirmType, nightRestMessage,
    doStartRest, // needed by logout cancel flow
  };
}
